import { ROUTES } from '../types/global/routes.types';
import type {
    ICampaignHostPackage,
    ICampaignHostPaymentRail,
    ICampaignHostPaystackCheckoutResult,
    ICampaignHostPricingResponse,
} from '../types/global/campaignHostSubscription.types';

export const CAMPAIGN_HOST_PAYSTACK_REF_KEY = 'campaign_host_sub_paystack_ref';
export const CAMPAIGN_HOST_CHECKOUT_PROVIDER_KEY = 'campaign_host_sub_checkout_provider';

export function getCampaignHostSubscriptionCallbackUrl(): string {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}${ROUTES.BRAND_SUBSCRIPTION_PAYSTACK_CALLBACK}`;
}

export function paystackCheckoutAvailable(rail?: ICampaignHostPaymentRail | null): boolean {
    return Boolean(rail?.use_paystack && rail?.paystack_available !== false);
}

export function flutterwaveCheckoutAvailable(rail?: ICampaignHostPaymentRail | null): boolean {
    return Boolean(rail?.use_flutterwave && rail?.flutterwave_available !== false);
}

export function stripeCheckoutAvailable(rail?: ICampaignHostPaymentRail | null): boolean {
    if (!rail) return true;
    return rail.use_stripe !== false && rail.stripe_available !== false;
}

/** Prefer API `payment_rail` from GET /pricing — do not infer from country alone. */
export function preferredCampaignHostProvider(
    rail?: ICampaignHostPaymentRail | null,
): 'paystack' | 'stripe' | 'flutterwave' {
    if (paystackCheckoutAvailable(rail) && rail?.preferred_provider === 'paystack') return 'paystack';
    if (
        paystackCheckoutAvailable(rail) &&
        rail?.preferred_provider !== 'stripe' &&
        rail?.preferred_provider !== 'flutterwave'
    ) {
        return 'paystack';
    }
    if (flutterwaveCheckoutAvailable(rail) && rail?.preferred_provider === 'flutterwave') return 'flutterwave';
    if (
        flutterwaveCheckoutAvailable(rail) &&
        !paystackCheckoutAvailable(rail) &&
        rail?.preferred_provider !== 'stripe'
    ) {
        return 'flutterwave';
    }
    return 'stripe';
}

export function shouldUsePaystackForCampaignHost(pricing?: ICampaignHostPricingResponse | null): boolean {
    const rail = pricing?.payment_rail;
    if (rail) {
        if (!paystackCheckoutAvailable(rail)) return false;
        if (rail.preferred_provider === 'paystack') return true;
        if (rail.use_paystack && !stripeCheckoutAvailable(rail) && !flutterwaveCheckoutAvailable(rail)) return true;
        return preferredCampaignHostProvider(rail) === 'paystack';
    }
    const packages = pricing?.packages ?? [];
    return packages.some(p => p.price_source === 'paystack' || Boolean(p.paystack_plan?.plan_code));
}

export function shouldUseFlutterwaveForCampaignHost(pricing?: ICampaignHostPricingResponse | null): boolean {
    const rail = pricing?.payment_rail;
    if (rail) {
        if (!flutterwaveCheckoutAvailable(rail)) return false;
        if (shouldUsePaystackForCampaignHost(pricing)) return false;
        if (rail.preferred_provider === 'flutterwave') return true;
        if (rail.use_flutterwave && !stripeCheckoutAvailable(rail)) return true;
        return preferredCampaignHostProvider(rail) === 'flutterwave';
    }
    const packages = pricing?.packages ?? [];
    return packages.some(p => p.price_source === 'flutterwave');
}

export function shouldUseStripeForCampaignHost(pricing?: ICampaignHostPricingResponse | null): boolean {
    if (shouldUsePaystackForCampaignHost(pricing) || shouldUseFlutterwaveForCampaignHost(pricing)) {
        return false;
    }
    return stripeCheckoutAvailable(pricing?.payment_rail);
}

export function isHostedCheckoutResult(data: unknown): data is ICampaignHostPaystackCheckoutResult {
    if (!data || typeof data !== 'object') return false;
    const r = data as Record<string, unknown>;
    const url = r.authorization_url ?? r.authorizationUrl;
    return r.provider === 'paystack' || r.provider === 'flutterwave' || (typeof url === 'string' && url.length > 0);
}

/** @deprecated Prefer isHostedCheckoutResult */
export function isPaystackCheckoutResult(data: unknown): data is ICampaignHostPaystackCheckoutResult {
    return isHostedCheckoutResult(data);
}

export function extractPaystackCheckoutResult(data: unknown): ICampaignHostPaystackCheckoutResult | null {
    if (!data || typeof data !== 'object') return null;
    const r = data as Record<string, unknown>;
    const authorization_url = String(r.authorization_url ?? r.authorizationUrl ?? '');
    if (!authorization_url) return null;
    const providerRaw = String(r.provider ?? 'paystack').toLowerCase();
    const provider = providerRaw === 'flutterwave' ? 'flutterwave' : 'paystack';
    return {
        provider,
        reference: String(r.reference ?? r.tx_ref ?? ''),
        authorization_url,
        access_code: (r.access_code ?? r.accessCode) as string | undefined,
        plan_code: (r.plan_code ?? r.planCode) as string | undefined,
        amount: typeof r.amount === 'number' ? r.amount : undefined,
        currency: (r.currency as string) ?? undefined,
        package: r.package as ICampaignHostPaystackCheckoutResult['package'],
        payment_rail: r.payment_rail as ICampaignHostPaymentRail | undefined,
    };
}

/** Paystack/Flutterwave charge in minor units from enriched package pricing. */
export function resolveCampaignHostPaystackAmountMinor(pkg: ICampaignHostPackage): number | null {
    if (typeof pkg.paystack_plan?.amount_minor === 'number' && pkg.paystack_plan.amount_minor > 0) {
        return pkg.paystack_plan.amount_minor;
    }
    if (typeof pkg.unit_amount === 'number' && pkg.unit_amount > 0) {
        return pkg.unit_amount;
    }
    if (typeof pkg.converted_amount_no_symbol === 'number' && pkg.converted_amount_no_symbol > 0) {
        return Math.round(pkg.converted_amount_no_symbol * 100);
    }
    return null;
}

export function buildCampaignHostSubscribeBody(
    packageId: string,
    opts?: { paymentMethodId?: string; callbackUrl?: string; pkg?: ICampaignHostPackage | null },
): Record<string, unknown> {
    const body: Record<string, unknown> = { packageId };
    if (opts?.paymentMethodId) body.paymentMethodId = opts.paymentMethodId;
    const amount = opts?.pkg ? resolveCampaignHostPaystackAmountMinor(opts.pkg) : null;
    if (amount != null && amount > 0) body.amount = amount;
    const callback = opts?.callbackUrl ?? getCampaignHostSubscriptionCallbackUrl();
    if (callback) {
        body.callback_url = callback;
        body.callbackUrl = callback;
    }
    return body;
}

export function buildCampaignHostPurchaseLtdBody(
    packageId?: string,
    opts?: { paymentMethodId?: string; callbackUrl?: string; pkg?: ICampaignHostPackage | null },
): Record<string, unknown> {
    const body: Record<string, unknown> = {};
    if (packageId) body.packageId = packageId;
    if (opts?.paymentMethodId) body.paymentMethodId = opts.paymentMethodId;
    const amount = opts?.pkg ? resolveCampaignHostPaystackAmountMinor(opts.pkg) : null;
    if (amount != null && amount > 0) body.amount = amount;
    const callback = opts?.callbackUrl ?? getCampaignHostSubscriptionCallbackUrl();
    if (callback) {
        body.callback_url = callback;
        body.callbackUrl = callback;
    }
    return body;
}

export function storeCampaignHostPaystackReference(
    reference: string,
    provider: 'paystack' | 'flutterwave' = 'paystack',
): void {
    try {
        sessionStorage.setItem(CAMPAIGN_HOST_PAYSTACK_REF_KEY, reference);
        sessionStorage.setItem(CAMPAIGN_HOST_CHECKOUT_PROVIDER_KEY, provider);
    } catch {
        /* ignore */
    }
}

export function readCampaignHostPaystackReference(): string | null {
    try {
        return sessionStorage.getItem(CAMPAIGN_HOST_PAYSTACK_REF_KEY);
    } catch {
        return null;
    }
}

export function readCampaignHostCheckoutProvider(): 'paystack' | 'flutterwave' | null {
    try {
        const raw = sessionStorage.getItem(CAMPAIGN_HOST_CHECKOUT_PROVIDER_KEY);
        if (raw === 'paystack' || raw === 'flutterwave') return raw;
        return null;
    } catch {
        return null;
    }
}

export function clearCampaignHostPaystackReference(): void {
    try {
        sessionStorage.removeItem(CAMPAIGN_HOST_PAYSTACK_REF_KEY);
        sessionStorage.removeItem(CAMPAIGN_HOST_CHECKOUT_PROVIDER_KEY);
    } catch {
        /* ignore */
    }
}

export const CAMPAIGN_HOST_RETURN_URL_KEY = 'campaign_host_sub_return_url';

export function storeCampaignHostReturnUrl(url: string): void {
    try {
        sessionStorage.setItem(CAMPAIGN_HOST_RETURN_URL_KEY, url);
    } catch {
        /* ignore */
    }
}

export function readCampaignHostReturnUrl(): string | null {
    try {
        return sessionStorage.getItem(CAMPAIGN_HOST_RETURN_URL_KEY);
    } catch {
        return null;
    }
}

export function clearCampaignHostReturnUrl(): void {
    try {
        sessionStorage.removeItem(CAMPAIGN_HOST_RETURN_URL_KEY);
    } catch {
        /* ignore */
    }
}

export function redirectToPaystackCheckout(result: ICampaignHostPaystackCheckoutResult): void {
    if (result.reference) storeCampaignHostPaystackReference(result.reference, result.provider);
    window.location.assign(result.authorization_url);
}
